import { useEffect, useState } from 'react';
import SideMenu from '../components/Menu';
import AddGrantModal from '../components/modals/AddGrantModal';
import WarningModal from '../components/modals/WarningModal';
import GrantsTabs from '../components/GrantsTabs';
import { useAuth } from '../context/AuthContext';
import PaperDetailsModal from '../components/modals/PaperDetailsModal';
import LoadingModal from '../components/modals/LoadingModal';
import { Study } from '../types/Study';

export interface Grant {
  _id: string;
  id: string;
  grantId: string;
  title: string;
  agency: string;
  closeDate: string;
  submissionDate: string;
  description: string;
  url: string;
  category: string;
  fundingStartDate: string;
  fundingEndDate: string;
  amountRequested: number;
  amountAwarded: number;
  status: string;
}

interface PreviousGrant {
  funder: string;
  funder_display_name: string;
  award_id: string | null;
}

interface PreviousPublication {
  title: string;
  id: string;
  publication_year: number;
  authors: string[];
  doi: string;
  grants: PreviousGrant[];
  citationCount: number;
  description: string;
}

const Grants = () => {
  const [grants, setGrants] = useState<Grant[]>([]);
  const [selectedGrant, setSelectedGrant] = useState<Grant | null>(null);
  const [selectedStudy, setSelectedStudy] = useState<Study | null>(null);
  const [loadingPastGrants, setLoadingPastGrants] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [analyticsModalLoading, setAnalyticsModalLoading] = useState<boolean>(false);
  const [pastGrants, setPastGrants] = useState<PreviousPublication[]>([]);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [warningModal, setWarningModal] = useState<boolean>(false);
  const [grantToDelete, setGrantToDelete] = useState<Grant | null>(null);
  const [activeTab, setActiveTab] = useState('currentGrants');
  const { user } = useAuth();

  const [currentPage, setCurrentPage] = useState(1);
  const papersPerPage = 20;

  const filteredPapers = pastGrants.filter(paper => {
    const lowerSearch = searchTerm.toLowerCase();
    return (
      paper.title?.toLowerCase().includes(lowerSearch) ||
      paper.authors?.join(', ').toLowerCase().includes(lowerSearch) ||
      paper.grants?.some(g => g.funder_display_name.toLowerCase().includes(lowerSearch))
    );
  });

  const indexOfLastPaper = currentPage * papersPerPage;
  const indexOfFirstPaper = indexOfLastPaper - papersPerPage;
  const currentPapers = filteredPapers.slice(indexOfFirstPaper, indexOfLastPaper);

  const handlePaperAnalytics = async (paperId: string | undefined) => {
    if (!paperId) return;
    setAnalyticsModalLoading(true);
    try {
      const url = `https://academichub-backend.onrender.com/paperanalytics?id=${paperId}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch works');
      }

      const data = await response.json();

      setSelectedStudy(data);
    } catch (error) {
      console.error('Error fetching works:', error);
    } finally {
      setAnalyticsModalLoading(false);
    }
  };

  const fetchPastGrants = async () => {
    try {
      setLoadingPastGrants(true);
      const response = await fetch(
        `https://academichub-backend.onrender.com/previousFunding?id=${user?.openAlexId}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch past grants');
      }

      const data = await response.json();
      setPastGrants(data);
    } catch (error) {
      console.error('Error fetching past grant data:', error);
    } finally {
      setLoadingPastGrants(false);
    }
  };

  const fetchGrantData = async () => {
    try {
      const response = await fetch(`https://academichub-backend.onrender.com/grants/get`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch grants');
      }
      setGrants(await response.json());
    } catch (error) {
      console.error('Error fetching grants:', error);
    }
  };

  const deleteGrant = async (grant: Grant) => {
    try {
      const response = await fetch(
        `https://academichub-backend.onrender.com/grants/remove/${grant.grantId}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        throw new Error('Failed to delete grant');
      }

      fetchGrantData();
    } catch (error) {
      console.error('Error deleting grant:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'currentGrants') {
      fetchGrantData();
    } else if (activeTab === 'pastGrants') {
      fetchPastGrants();
    }
  }, [activeTab]);

  useEffect(() => {
    console.log(pastGrants);
  }, [pastGrants]);

  const activateAddModal = () => {
    setSelectedGrant(null);
    setModalVisible(true);
  };

  return (
    <>
      <SideMenu />
      <div id="grant-management" className="w-full h-screen bg-accent p-3 overflow-hidden">
        <div className="bg-white rounded-lg h-full flex flex-col p-6">
          <div className="mb-3 pl-1">
            <GrantsTabs activeTab={activeTab} setActiveTab={setActiveTab} />
          </div>
          {activeTab === 'currentGrants' && (
            <>
              <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-center">Grant Management</h1>
                <button
                  onClick={() => activateAddModal()}
                  className="text-xs bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-800"
                >
                  Add Grant
                </button>
              </div>
              <div className="overflow-auto mt-4 flex flex-col gap-4 h-full">
                {grants.length === 0 ? (
                  <div className="flex-grow flex items-center justify-center">
                    <p className="text-sm text-gray-500">No grants added.</p>
                  </div>
                ) : (
                  grants.map((grant, index) => (
                    <div
                      key={`${grant._id || grant.id || index}`}
                      className="bg-gray-50 border border-gray-200 rounded-lg shadow-sm p-4"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <h2 className="text-xl font-semibold text-[#1d244f]">
                          {grant.title || 'Untitled Grant'}
                        </h2>

                        {grant.status && (
                          <span
                            className={`px-4 py-1 text-sm font-semibold rounded-full shadow-sm border
                            ${
                              grant.status === 'draft'
                                ? 'bg-gray-100 text-gray-700 border-gray-300'
                                : grant.status === 'submitted'
                                ? 'bg-blue-100 text-blue-700 border-blue-300'
                                : grant.status === 'awarded'
                                ? 'bg-green-100 text-green-700 border-green-300'
                                : grant.status === 'rejected'
                                ? 'bg-red-100 text-red-700 border-red-300'
                                : ''
                            }`}
                          >
                            {grant.status.toUpperCase()}
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-800">
                        <div className="space-y-2">
                          {grant.agency && (
                            <p>
                              <strong>Agency:</strong> {grant.agency}
                            </p>
                          )}
                          {grant.closeDate && (
                            <p>
                              <strong>Close Date:</strong>{' '}
                              {new Date(grant.closeDate).toLocaleDateString('en-GB')}
                            </p>
                          )}
                          {grant.submissionDate && (
                            <p>
                              <strong>Submission Date:</strong>{' '}
                              {new Date(grant.submissionDate).toLocaleDateString('en-GB')}
                            </p>
                          )}
                          {grant.description && (
                            <p className="whitespace-pre-wrap">
                              <strong>Description:</strong> {grant.description}
                            </p>
                          )}
                          {grant.url && (
                            <p>
                              <strong>URL:</strong>{' '}
                              <a
                                href={grant.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline break-all"
                              >
                                {grant.url}
                              </a>
                            </p>
                          )}
                        </div>
                        <div className="space-y-2 md:border-l md:pl-6">
                          {grant.category && (
                            <div>
                              <strong>Category:</strong>
                              <div className="mt-1 flex flex-wrap gap-2">
                                {grant.category.split(',').map((cat: string, idx: number) => (
                                  <span
                                    key={idx}
                                    className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full border border-blue-200"
                                  >
                                    {cat.trim()}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {grant.fundingStartDate && (
                            <p>
                              <strong>Funding Start:</strong>{' '}
                              {new Date(grant.fundingStartDate).toLocaleDateString('en-GB')}
                            </p>
                          )}
                          {grant.fundingEndDate && (
                            <p>
                              <strong>Funding End:</strong>{' '}
                              {new Date(grant.fundingEndDate).toLocaleDateString('en-GB')}
                            </p>
                          )}
                          {grant.amountRequested && (
                            <p>
                              <strong>Amount Requested:</strong> $
                              {grant.amountRequested.toLocaleString()}
                            </p>
                          )}
                          {grant.amountAwarded && (
                            <p>
                              <strong>Amount Awarded:</strong> $
                              {grant.amountAwarded.toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-end gap-4 mt-4 text-sm">
                        <button
                          onClick={() => {
                            setSelectedGrant(grant);
                            setModalVisible(true);
                          }}
                          className="text-blue-500 hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            setWarningModal(true);
                            setGrantToDelete(grant);
                          }}
                          className="text-red-500 hover:underline"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
          {activeTab === 'pastGrants' && !loadingPastGrants && (
            <div className="overflow-y-auto max-h-[80vh] px-4">
              <div className="sticky top-0 z-10 bg-white pb-2 pt-4">
                <div className="flex justify-between items-center pb-4">
                  <h1 className="text-2xl font-bold text-center">
                    Research Output from Past Funding
                  </h1>
                  <div className="sm:ml-auto flex items-center mt-2 sm:mt-0 mb-2 sm:mb-0">
                    <div className="relative w-full sm:w-auto flex items-center">
                      <div className="flex"></div>
                      <input
                        type="text"
                        placeholder="Search"
                        className="border rounded-md px-2 py-1 text-sm w-full min-w-[160px]"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                {currentPapers.map(paper => (
                  <div key={paper.id} className="border-b pb-4">
                    <div className="flex justify-between items-start">
                      <h3
                        className="text-lg font-medium cursor-pointer"
                        style={{ color: '#0B132B' }}
                        onClick={() => handlePaperAnalytics(paper.id)}
                      >
                        {paper.title}
                      </h3>
                    </div>
                    <p className="text-sm" style={{ color: '#5BC0BE' }}>
                      {paper.authors.join(', ')}
                    </p>
                    <p className="text-sm mt-1 text-gray-700">{paper.description}</p>
                    <div className="flex mt-2 text-xs text-gray-500 justify-between items-center">
                      <div>
                        {paper.publication_year && (
                          <span className="mr-4">Year: {paper.publication_year}</span>
                        )}
                        {paper.citationCount !== undefined && (
                          <span className="mr-4">Citations: {paper.citationCount}</span>
                        )}
                      </div>
                    </div>
                    {paper.grants && paper.grants.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm font-semibold text-gray-800 mb-1">Funded by:</p>
                        <div className="flex flex-wrap gap-2">
                          {paper.grants.map((grant: PreviousGrant, idx: number) => (
                            <button
                              key={idx}
                              className="
                                bg-[#1D3557] text-white text-xs font-medium px-3 py-1 
                                rounded-full shadow-sm hover:bg-[#16324f] transition-all duration-150
                              "
                              disabled
                            >
                              {grant.funder_display_name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="sticky bottom-0 z-10 bg-white pt-2 pb-4 mt-4 border-t">
                <div className="flex justify-center mt-6 space-x-2">
                  {[...Array(Math.ceil(filteredPapers.length / papersPerPage)).keys()].map(num => (
                    <button
                      key={num}
                      className={`px-3 py-1 rounded-md text-sm ${
                        currentPage === num + 1 ? 'bg-blue-500 text-white' : 'bg-gray-200'
                      }`}
                      onClick={() => setCurrentPage(num + 1)}
                    >
                      {num + 1}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {activeTab === 'pastGrants' && loadingPastGrants && (
            <div className="overflow-y-auto max-h-[80vh] px-4">
              <div className="sticky top-0 z-10 bg-white pb-2 pt-4">
                <div className="flex justify-between items-center pb-4">
                  <h1 className="text-2xl font-bold text-center">
                    Research Output from Past Funding
                  </h1>
                </div>
              </div>
              <div className="flex justify-center items-center pt-32">
                <div
                  className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4"
                  style={{
                    borderColor: '#5BC0BE transparent #5BC0BE transparent',
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <AddGrantModal
        onClose={() => setModalVisible(false)}
        visible={modalVisible}
        initialValues={selectedGrant || undefined}
        onGrantAdded={() => {
          fetchGrantData();
        }}
      />
      <WarningModal
        visible={warningModal}
        description="Are you sure you want to delete this grant?"
        onCancel={() => setWarningModal(false)}
        onConfirm={() => {
          if (grantToDelete) {
            deleteGrant(grantToDelete);
          }
          setWarningModal(false);
        }}
      />
      {selectedStudy && (
        <PaperDetailsModal
          isOpen={!!selectedStudy}
          onClose={() => setSelectedStudy(null)}
          study={selectedStudy}
        />
      )}
      {analyticsModalLoading && <LoadingModal onClose={() => setSelectedStudy(null)} />}
    </>
  );
};

export default Grants;
